import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import { withStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import { format } from 'date-fns'
import AppShell from './components/layout/AppShell'
import PageCard from './components/ui/PageCard'
import EmptyState from './components/ui/EmptyState'

const styles = (theme) => ({
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
  },
  table: {
    minWidth: 1100,
  },
  nameText: {
    fontWeight: 600,
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  link: {
    color: theme.palette.primary.main,
  },
})

const STATUS_COLOR = {
  linked: '#22c55e',
  unmatched: '#94a3b8',
  id_only: '#f59e0b',
  name_only: '#0ea5e9',
  split: '#a855f7',
  ambiguous: '#ef4444',
}

function StatusMark({ status }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: STATUS_COLOR[status] || STATUS_COLOR.unmatched,
      }}
    />
  )
}

function displayValue(value) {
  if (value == null || value === '') return '—'
  return value
}

function formatScore(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  if (Number.isInteger(n)) return String(n)
  return String(Math.round(n * 100) / 100)
}

function formatTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return format(date, 'yyyy/MM/dd HH:mm')
}

function codeOf(email) {
  if (!email) return '—'
  return String(email).split('@')[0]
}

class Longgood extends Component {
  state = {
    group: {},
    rows: [],
    loaded: false,
    failed: false,
  }

  componentDidMount() {
    this.getGroupData()
    this.getSessions()
  }

  async getGroupData() {
    const uniqid = this.props.match.params.uniqid
    try {
      const result = await fetch(`/api/getGroupData?uniqid=${uniqid}`, {
        mode: 'cors',
        method: 'GET',
      })
      const data = await result.json()
      if (data.success) {
        this.setState({ group: data.data })
      } else {
        this.props.history.push('/groups')
      }
    } catch (err) {
      this.props.history.push('/groups')
    }
  }

  async getSessions() {
    const uniqid = this.props.match.params.uniqid
    try {
      const result = await fetch(`/api/longgood/sessions?uniqid=${uniqid}`, {
        mode: 'cors',
        method: 'GET',
      })
      const data = await result.json()
      if (data.success) {
        this.setState({
          rows: data.rows || [],
          loaded: true,
          failed: false,
        })
      } else {
        this.setState({ loaded: true, failed: true })
      }
    } catch (err) {
      this.setState({ loaded: true, failed: true })
    }
  }

  render() {
    const { classes } = this.props
    const { group, rows, loaded, failed } = this.state
    const uniqid = this.props.match.params.uniqid

    return (
      <AppShell
        uniqid={uniqid}
        title={`龍骨王 · ${group.name || ''}`}
        subtitle={group.description}
      >
        <PageCard>
          {!loaded ? (
            <EmptyState title="讀取中" description="正在對接龍骨王資料" />
          ) : failed ? (
            <EmptyState title="無法讀取" description="龍骨王資料暫時讀取失敗" />
          ) : rows.length === 0 ? (
            <EmptyState title="此群組沒有成員" description="先到成員列表加入個案" />
          ) : (
            <div className={classes.tableWrap}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>成員</TableCell>
                    <TableCell align="center">對接</TableCell>
                    <TableCell>活動</TableCell>
                    <TableCell>執行時間</TableCell>
                    <TableCell align="right">分數</TableCell>
                    <TableCell align="right">執行等級</TableCell>
                    <TableCell>生日</TableCell>
                    <TableCell align="center">性別</TableCell>
                    <TableCell>產品</TableCell>
                    <TableCell>報告</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const name = `${row.lastname || ''}${row.firstname || ''}` || '—'
                    return (
                      <TableRow key={`${row.id}-${row.session_id || 'none'}`}>
                        <TableCell>
                          <Typography className={classes.nameText}>{name}</Typography>
                          <Typography className={classes.muted}>{codeOf(row.email)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <StatusMark status={row.match_status} />
                        </TableCell>
                        <TableCell>{displayValue(row.activity_name)}</TableCell>
                        <TableCell>{formatTime(row.execute_time)}</TableCell>
                        <TableCell align="right">{formatScore(row.score)}</TableCell>
                        <TableCell align="right">{formatScore(row.execution_level)}</TableCell>
                        <TableCell>{displayValue(row.birthday)}</TableCell>
                        <TableCell align="center">{displayValue(row.gender)}</TableCell>
                        <TableCell>{displayValue(row.product)}</TableCell>
                        <TableCell>
                          {row.report_url ? (
                            <a
                              className={classes.link}
                              href={row.report_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              開啟
                            </a>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </PageCard>
      </AppShell>
    )
  }
}

Longgood.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
}

export default withStyles(styles)(Longgood)
